/**
 * ide-shell-plugin — the IDE shell as a studio-sibling `ComposableGenomePlugin`.
 *
 * Mirrors `shellPlugin` / `devPlugin`: one `component` editor (`shell`) routed at
 * `/ide`, composed into the ONE studio genome (gate 4 — `/ide` resolves to the
 * registered `shell` editor). The editor is `React.lazy` so FlexLayout + the IDE
 * shell stay in a split chunk out of the app entry (gate 8). The route id is
 * derived from the path (`/ide` → `ide`); `routesTo: 'component/shell'`
 * auto-expands to this plugin's `:component/shell` entity.
 *
 * #148-C5 — the SAME `shell` editor is ALSO routed at the nested host/project URL
 * `/:hostId/projects/:projectSlug` (a second `route` entity, SAME `routesTo`
 * target). The SDK router already matches multi-segment `:param` routes
 * (`matchRoute` in `route-params.tsx`) — no router change. This route just RENDERS
 * the IDE shell at the deep-linkable URL; the param-driven host/project HYDRATION
 * (reading `useRouteParams()` → `{ hostId, projectSlug }` into the ide-store) is
 * C6's job, not this item. The literal route is the gate-4 resolve target; the
 * cdp-smoke enumerator substitutes a concrete fixture path to walk it.
 *
 * @module @ubiclouds/v5-studio/ide/ide-shell-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The IDE shell plugin id. */
export const IDE_SHELL_ID = 'v5-studio-ide';

/**
 * The host-only IDE route (#S-19). A single-segment `:hostId` param route — a
 * connected host with no project yet. Connecting a host navigates here (so the URL
 * reflects the active host); IdeShell hydrates `setHost(hostId, hostId)` from it.
 * Routes to the SAME `shell` editor. A LITERAL route still wins (exact-first), so a
 * host id equal to a literal route (`dev`/`app`/…) is guarded at navigate time
 * (`host-url.isReservedHostSegment`) — its project deep-link still works.
 */
export const IDE_HOST_ROUTE_PATH = '/:hostId';

/**
 * The nested host/project IDE route (#148-C5). A multi-segment param route whose
 * `:hostId` / `:projectSlug` are captured by the SDK router's `matchRoute` and
 * read via `useRouteParams()`. It routes to the SAME `shell` editor as `/ide`.
 */
export const IDE_PROJECT_ROUTE_PATH = '/:hostId/projects/:projectSlug';

const LazyIdeShell = lazy(() => import('./IdeShell.js'));

export const ideShellPlugin: ComposableGenomePlugin = definePlugin(IDE_SHELL_ID)
  .name('IDE Shell')
  .trustTier('core')
  .editor('shell', LazyIdeShell)
  .route('/ide', { routesTo: 'component/shell', title: 'IDE' })
  // #S-19: the host-only URL renders the SAME IDE shell editor (connected host,
  // no project). Explicit id (the auto-derived one would carry the `:` colon).
  .route(IDE_HOST_ROUTE_PATH, {
    id: 'ide-host',
    routesTo: 'component/shell',
    title: 'IDE — Host',
  })
  // #148-C5: the nested host/project URL renders the SAME IDE shell editor. An
  // explicit route id (the auto-derived one would carry the `:param` colons).
  .route(IDE_PROJECT_ROUTE_PATH, {
    id: 'ide-project',
    routesTo: 'component/shell',
    title: 'IDE — Project',
  })
  .build();
